import { useMultiFormContext } from "../../../../../Common/components/MultiForm";
import React, { useEffect, useMemo, useState } from "react";
import SimpleTable from "my-simple-react-table";
import { formatDate, formatFormTitle, formatStatus } from "../../../../../../util/Helpers";
import { Button } from "react-bootstrap";
import { useApproveCipChecksMutation } from "../../../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import { Form, Formik } from "formik";
import * as Yup from "yup"
import TextInput from "../../../../../Common/components/text-input";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../../../_metronic/_helpers";
import TableSkeleton from "../../../../../../util/TableSkeleton";

export function CIPInformation({ dataKey, title, initialValues, isEditable, isRequired, refresh, isFetching }) {
    const { upsertValue } = useMultiFormContext();
    const { userId, userGroup, cip, nationality } = initialValues;
    const [showApprovalForm, setShowApprovalForm] = useState(false)

    const [
        approveCIP, // This is the mutation trigger
        {
            isLoading: isApproving,
            isSuccess: approvedSuccessfully,
        }, // This is the destructured mutation result
    ] = useApproveCipChecksMutation()

    /* Close Modal on KYC Approval */
    useEffect(() => {
        if (approvedSuccessfully) {
            cogoToast.success("CIP Checks Approved Successfully")
            setShowApprovalForm(false)
        }
    }, [approvedSuccessfully]);

    // select and approve cip checks
    const approve = ({ reason }) => {
        const checks = []

        let document = false
        let identity = false
        let watchlist = false

        // Select the latest unclear document, identity and watchlist checks if any
        for (const report of cip) {
            const isNotClear = report.finalResult !== "clear" && report.result !== "clear"
            if (!isNotClear) continue
            switch (report.reportType) {
                case "document":
                    if (!document) checks.push({ autoId: report?.autoId })
                    document = true
                    break
                case "identity_enhanced":
                    if (!identity) checks.push({ autoId: report?.autoId })
                    identity = true
                    break
                case "watchlist_enhanced":
                    if (!watchlist) checks.push({ autoId: report?.autoId })
                    watchlist = true
                    break
                default:
                    break
            }
        }

        approveCIP({
            userId,
            userGroup,
            checks,
            reason
        })
    }

    const isValid = useMemo(() => {
        let identityIsValid = false;
        let watchlistIsValid = false;
        let documentIsValid = false;

        for (const report of cip) {
            const isClear = report.finalResult === "clear" || report.result === "clear"
            if (!isClear) continue
            switch (report.reportType) {
                case "document":
                    documentIsValid = documentIsValid || isClear
                    break
                case "identity_enhanced":
                    identityIsValid = identityIsValid || isClear
                    break
                case "watchlist_enhanced":
                    watchlistIsValid = watchlistIsValid || isClear
                    break
                default:
                    break
            }
        }

        return watchlistIsValid && documentIsValid && (nationality !== "USA" || identityIsValid)
    }, [cip, nationality])

    useEffect(() => {
        upsertValue(dataKey, { isValid: !isRequired || isValid })
    }, [dataKey, isRequired, isValid, upsertValue])

    const validStyle = isValid ? "primary" : "danger"

    return (
        <span>
            {formatFormTitle({ title, isValid })}
            {
                showApprovalForm
                    ? <div className={`py-3 mb-3 rounded-sm text-${validStyle} bg-light-${validStyle}`}>
                        <Formik
                            initialValues={{ reason: "" }}
                            validationSchema={
                                Yup.object({ reason: Yup.string().required("required") })
                            }
                            onSubmit={(values) => approve(values)}
                        >
                            <Form>
                                <TextInput
                                    label="Reason for Approving Checks*"
                                    name="reason"
                                    type="text"
                                    className="border-bottom border-bottom-dark border-transparent bg-transparent h-40px w-100"
                                    style={{
                                        outline: "none"
                                    }}
                                />
                                <div className={`px-4 outline-none d-flex justify-content-end`}>
                                    <button
                                        type="button"
                                        onClick={() => setShowApprovalForm(false)}
                                        disabled={isApproving}
                                        className="btn btn-outline-dark btn-elevate px-4"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isApproving}
                                        className="btn btn-dark btn-elevate px-4 ml-4"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </Form>
                        </Formik>
                    </div>
                    : <div
                        className={`d-flex align-items-center w-100 p-3 mb-3 rounded-sm text-${validStyle} bg-light-${validStyle}`}
                    >
                        <span className={"flex-fill font-weight-bold"}>
                            {`${nationality === "USA" ? 'identity, ' : ""}watchlist and document checks ${isValid ? "are all" : "must all be"} clear.`}
                        </span>
                        {
                            (isRequired && isEditable && !isValid && cip[0]?.reportType === "document") &&
                            <Button
                                type="button"
                                variant={"dark"}
                                onClick={() => setShowApprovalForm(true)}
                            >
                                Accept Checks Anyway
                            </Button>
                        }
                    </div>
            }
            <button className="btn py-0 px-2" onClick={() => refresh()}>
                <span className="svg-icon svg-icon-lg svg-icon-primary">
                    <SVG
                        title="Refresh Table"
                        src={toAbsoluteUrl("/media/svg/icons/Code/Time-schedule.svg")}
                    />
                </span>
            </button>
            {
                isFetching
                    ? <TableSkeleton />
                    : <SimpleTable
                        key={dataKey}
                        searchBox={false}
                        data={cip}
                        columns={[
                            { title: "Check ID", key: "checkId" },
                            { title: "Type", key: "reportType" },
                            { title: "Created On", format: ({ created }) => formatDate(created) },
                            { title: "Status", format: ({ status }) => formatStatus(status) },
                            { title: "Result", format: ({ result, finalResult }) => formatStatus(finalResult || result) },
                        ]}
                    />
            }
        </span>
    )
}